/**
 * v6.5.1 — Manus-direction LANDING DESIGN STUDY
 *
 * Mounted at /preview/landing-manus for visual review only. Identical
 * CONTENT to the live LandingPage.js (same arrays, same copy, same
 * routes, same CTA labels). Only the visual treatment is different:
 * warm cream palette, grid-blueprint background, amber pills, editorial
 * type, thin-border cards, two-column hero.
 *
 * Design tokens (from spec):
 *   --bg-main: #f6f0e6  warm cream
 *   --bg-panel: #fbf8f1 cream panel
 *   --text-main: #24211d
 *   --text-muted: #6f675c
 *   --border-soft: #ddd3c2
 *   --amber: #d69b22
 *   --amber-dark: #b8790f
 *
 * If the user approves this direction, the change to /=#LandingPage is a
 * single-line route swap in App.js — content swap is zero work.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Briefcase, Cpu, Eye, UserCheck, Check, Linkedin } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";
import RequestAccountModal from "@/components/RequestAccountModal";
import "@/styles/word-rotator.css";

// ── Content (mirrors LandingPage.js verbatim — DO NOT EDIT in this file
//    once we're sure of the direction. Move to a shared module instead.) ──

const BRAND = "Kalib AI";

// v6.5.2 — Per-scenario sample-Reveal URLs live on each entry of
// HERO_SCENARIOS below. The legacy single-URL constant is gone.

// Fallback list used only when /api/accounts fails. Live accounts are
// always sourced from the API in production; this fallback exists so
// the page still renders something sensible if the network call fails.
const FALLBACK_ACCOUNTS = [
  { id: "deutsche_bank", name: "Deutsche Bank", status: "live", order: 1 },
  { id: "citigroup", name: "Citigroup", status: "live", order: 2 },
  { id: "lloyds_banking_group", name: "Lloyds Banking Group", status: "live", order: 3 },
  { id: "klarna", name: "Klarna", status: "live", order: 4 },
  { id: "revolut", name: "Revolut", status: "live", order: 5 },
];

// Logo registry for the Live Now tiles. Source-of-truth for live
// accounts is /api/accounts; the marketing surface needs a brand mark
// per id. If an id is missing here the tile falls back to a name pill.
// Live tiles render at full saturation (vs greyscale on Coming Soon)
// to signal active coverage; the amber dot is preserved as a small
// status indicator in the top-right corner.
const LIVE_LOGOS = {
  deutsche_bank:        { src: "/logos/deutsche-bank.svg", alt: "Deutsche Bank" },
  citigroup:            { src: "/logos/citi.svg",          alt: "Citi" },
  lloyds_banking_group: { src: "/logos/lloyds.png",        alt: "Lloyds Banking Group" },
  klarna:               { src: "/logos/klarna.svg",        alt: "Klarna" },
  revolut:              { src: "/logos/revolut.svg",       alt: "Revolut" },
};

// Coming Soon list is intentionally hardcoded on the landing page.
// It's a marketing surface, not the product's coverage truth — adding
// an account here doesn't enable backend coverage. When an account
// flips to live in /api/accounts, it'll show up in liveAccounts above
// automatically; the entry here can stay until coverage actually ships
// or be removed at any time. Logos live in /app/frontend/public/logos/.
const COMING_SOON = [
  { id: "jpmorgan_chase", name: "JPMorgan Chase", logo: "/logos/jpmorgan-chase.svg" },
  { id: "goldman_sachs",  name: "Goldman Sachs",  logo: "/logos/goldman-sachs.svg" },
  { id: "hsbc",           name: "HSBC",           logo: "/logos/hsbc.svg" },
  { id: "barclays",       name: "Barclays",       logo: "/logos/barclays.svg" },
  { id: "nubank",         name: "Nubank",         logo: "/logos/nubank.svg" },
];

// ── HERO_SCENARIOS — rotating "How Kalib AI Works" demo cards ──────────
//
// Convention: every visible token a real user *picks* in Reveal Setup
// (account, role family, offer lane, commercial motion verb) renders in
// AMBER BOLD (#b8790f, font-weight 700) — that's what signals "this is
// tune-able" to the visitor. Static descriptive copy stays neutral.
//
// Cards rotate every 10s. Hovering the card pauses the countdown.
// Clicking a dot below the card jumps directly to that scenario.
// New scenarios get appended to this array — no other code needs to
// change. moveSnippet is the one truncated/faded line under "5 Moves"
// that hints at the linked Reveal.
const HERO_SCENARIOS = [
  {
    id: "deutsche_bank",
    sampleUrl: "/sample/db",
    signalEyebrow: "Signal this week · w/c 4 May 2026",
    signal: (
      <>
        <strong style={{ fontWeight: 700, color: "#b8790f" }}>Deutsche Bank</strong>
        {" "}(DB)'s leadership reset is pulling efficiency, controls, M&amp;A execution, AI governance, and Private Bank restructuring into one commercial window.
      </>
    ),
    youAre: (
      <>
        The Global Account <span style={{ color: "#b8790f", fontWeight: 700 }}>Leader</span> at a large <span style={{ color: "#b8790f", fontWeight: 700 }}>Services</span> firm, managing a $100M+ DB book with a mandate to <span style={{ color: "#b8790f", fontWeight: 700 }}>grow</span> existing business 25% YoY.
      </>
    ),
    moveSnippet: (
      <>DB's efficiency push may be where existing delivery becomes a bigger control-and-growth conversation…</>
    ),
    topRightLogo: { src: "/logos/deutsche-bank.svg", alt: "Deutsche Bank" },
  },
  {
    id: "lbg",
    sampleUrl: "/sample/lbg",
    signalEyebrow: "Signal this week · w/c 4 May 2026",
    signal: (
      <>
        <strong style={{ fontWeight: 700, color: "#b8790f" }}>Lloyds Banking Group</strong>
        {" "}(LBG) is moving AI into executive ownership with Sameer Gupta's appointment. Plus: AI-agent activity, digital-homebuyer partnerships, emerging-risk experiments are all landing in an interesting decision window.
      </>
    ),
    youAre: (
      <>
        An <span style={{ color: "#b8790f", fontWeight: 700 }}>Individual Contributor</span> at an <span style={{ color: "#b8790f", fontWeight: 700 }}>Advisory</span> firm, with a $5M p.a. <span style={{ color: "#b8790f", fontWeight: 700 }}>new business</span> goal.
      </>
    ),
    moveSnippet: (
      <>Sameer Gupta's arrival may shift the conversation from AI ambition to who owns the hard choices…</>
    ),
    topRightLogo: { src: "/logos/lloyds.png", alt: "Lloyds Banking Group" },
  },
  {
    id: "klarna",
    sampleUrl: "/sample/klarna",
    signalEyebrow: "Signal this week · w/c 4 May 2026",
    signal: (
      <>
        <strong style={{ fontWeight: 700, color: "#b8790f" }}>Klarna</strong>
        {" "}is stretching into increasingly harder merchant segments. All this points to just one test for Software/SaaS firms: Can your platform help activate new markets fast, without creating operational drag?
      </>
    ),
    youAre: (
      <>
        A <span style={{ color: "#b8790f", fontWeight: 700 }}>Solutioning Manager</span> at a <span style={{ color: "#b8790f", fontWeight: 700 }}>Software/SaaS</span> firm, shaping several commercial cases and FDE deployments for Klarna(-like) clients.
      </>
    ),
    moveSnippet: (
      <>Klarna's vertical push may expose the real buying test: not features. But how fast a new merchant segment goes live…</>
    ),
    topRightLogo: { src: "/logos/klarna.svg", alt: "Klarna" },
  },
  {
    id: "citigroup",
    sampleUrl: "/sample/citi",
    signalEyebrow: "Signal this week · 6–12 May 2026",
    signal: (
      <>
        With strong Q1 results, <strong style={{ fontWeight: 700, color: "#b8790f" }}>Citigroup</strong> has now brought in a ex-Google CIO to scale AI and optimise technology. For Cloud providers, the signal this week is about who can turn AI ambition into controlled production workloads?
      </>
    ),
    youAre: (
      <>
        A <span style={{ color: "#b8790f", fontWeight: 700 }}>Sales Manager</span> at a <span style={{ color: "#b8790f", fontWeight: 700 }}>Cloud</span> provider, with a $25M net-new goal to <span style={{ color: "#b8790f", fontWeight: 700 }}>win new</span> cloud services around AI workload scaling, data readiness and regional compliance.
      </>
    ),
    moveSnippet: (
      <>Citi's new CIO may make AI scale less of a strategy pitch and more of an architecture runway test…</>
    ),
    // topRightLogo replaces the default "SAMPLE" tag on this scenario.
    // If the logo asset ever fails to load, the <img> onError handler
    // swaps in a text fallback so the row never collapses.
    topRightLogo: { src: "/logos/citi.svg", alt: "Citi" },
  },
  {
    id: "revolut",
    sampleUrl: "/sample/revolut",
    signalEyebrow: "Signal this week · 6–12 May 2026",
    signal: (
      <>
        <strong style={{ fontWeight: 700, color: "#b8790f" }}>Revolut</strong>'s expansion is turning compliance into a delivery-capacity test: the US banking charter push, UK licence, AI scale. All need auditable controls that can actually ship.
      </>
    ),
    youAre: (
      <>
        A junior <span style={{ color: "#b8790f", fontWeight: 700 }}>Account Manager</span> at a <span style={{ color: "#b8790f", fontWeight: 700 }}>Services</span> firm, protecting an $8M Revolut annual run-rate and looking for the next <span style={{ color: "#b8790f", fontWeight: 700 }}>land-and-expand</span> wedge.
      </>
    ),
    moveSnippet: (
      <>Revolut's US charter push may make a mapped controls workstream the cleanest expansion wedge…</>
    ),
    topRightLogo: { src: "/logos/revolut.svg", alt: "Revolut" },
  },
];

const LENS_GROUPS = [
  { header: "What you sell", sub: "Pick the offer you sell",
    items: ["Software", "Services", "Cloud", "Advisory"] },
  { header: "What you're trying to do", sub: "Pick what you're trying to move",
    items: ["Win new business", "Grow existing business", "Build solutioning & commercials", "Deliver existing business"] },
  { header: "Your operating level", sub: "Pick your operating altitude",
    items: ["Individual Contributor", "Manager", "Leader"] },
];

const COMPARISON_ROWS = [
  { outside: "Earnings language.", inside: "Sponsor mood." },
  { outside: "Leadership moves.", inside: "Procurement reality." },
  { outside: "Regulatory pressure.", inside: "Delivery scars." },
  { outside: "Technology spend cues.", inside: "Live pursuits." },
  { outside: "Peer behaviour.", inside: "Incumbent politics." },
  { outside: "Sector movement.", inside: "What the client will actually tolerate." },
];

// Field-credibility section. Single founder card — image + name +
// LinkedIn badge + the merged credibility paragraphs. Adding a second
// card would mean reintroducing the grid wrapper around the renderer
// below; for now this surface is intentionally compressed to one card
// so the founder anchors the credibility claim.
const FOUNDER = {
  name: "Srini Annamaraju",
  image: "/founder/srini.jpg",
  linkedin: "https://www.linkedin.com/in/sriniuk/",
  paragraphs: [
    "Three decades inside enterprise technology, FS-first. Sold into, defended, and grew accounts at the world's largest FS institutions.",
    "AI reads the signals. The rubric is commercial judgement, built from doing the work.",
  ],
};

// ── small visual helpers ─────────────────────────────────────────────

function Eyebrow({ children, className = "" }) {
  return (
    <p
      className={`text-[11px] font-semibold uppercase ${className}`}
      style={{ letterSpacing: "0.18em", color: "#b8790f" }}
    >
      {children}
    </p>
  );
}

function HairlineRule() {
  return <div className="w-10 h-px" style={{ backgroundColor: "#d69b22" }} />;
}

function AmberPill({ active = false, children }) {
  return (
    <span
      className="inline-block text-[12px] px-3 py-1.5 transition-colors border"
      style={{
        backgroundColor: active ? "#fdf3df" : "#fbf8f1",
        borderColor: active ? "#d69b22" : "#ddd3c2",
        color: active ? "#7a4f0c" : "#3a342e",
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </span>
  );
}

// Faint blueprint-grid background. CSS-only, GPU-cheap. Single repeating
// pattern with two grid families (10px minor + 100px major) so the page
// reads as "engineering paper" without becoming busy.
function GridBackground() {
  return (
    <div
      className="fixed inset-0 -z-10 pointer-events-none"
      style={{
        backgroundColor: "#f6f0e6",
        backgroundImage: `
          linear-gradient(rgba(184, 121, 15, 0.04) 1px, transparent 1px),
          linear-gradient(to right, rgba(184, 121, 15, 0.04) 1px, transparent 1px),
          linear-gradient(rgba(184, 121, 15, 0.07) 1px, transparent 1px),
          linear-gradient(to right, rgba(184, 121, 15, 0.07) 1px, transparent 1px)
        `,
        backgroundSize: "20px 20px, 20px 20px, 100px 100px, 100px 100px",
      }}
    />
  );
}

// Decorative amber arc that anchors the hero artefact on the right.
function AmberArc({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 400 400" fill="none" aria-hidden="true">
      <circle cx="200" cy="200" r="195" stroke="#d69b22" strokeWidth="0.8" opacity="0.45" />
      <circle cx="200" cy="200" r="155" stroke="#d69b22" strokeWidth="0.8" opacity="0.3" />
      <circle cx="200" cy="200" r="115" stroke="#d69b22" strokeWidth="0.5" opacity="0.2" />
    </svg>
  );
}


// ── component ─────────────────────────────────────────────────────────

export default function LandingPage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState(FALLBACK_ACCOUNTS);

  // ── Hero scenario rotator ────────────────────────────────────────────
  // 10-second auto-advance with a thin amber progress bar at the top of
  // the card. Hovering the card sets `paused=true` so visitors who're
  // mid-read aren't yanked. Clicking a dot below the card jumps to that
  // scenario and resets the tick.
  //
  // IMPORTANT: elapsed time lives in a ref, not state. Nesting
  // `setScenarioIdx` inside a `setTickProgress` updater used to trigger
  // React 18 StrictMode's intentional double-invocation of pure updater
  // callbacks — which advanced the index twice per tick (0→1→0) and the
  // card appeared to never change. Keeping the advance side-effect out
  // of any updater function fixes it.
  const [scenarioIdx, setScenarioIdx] = useState(0);
  const [tickProgress, setTickProgress] = useState(0); // 0..1 over 10s
  const [paused, setPaused] = useState(false);
  const elapsedRef = useRef(0);
  const scenario = HERO_SCENARIOS[scenarioIdx];

  // 2026-05-09 — Request Account modal state. The trigger sits inline
  // beneath the Coming Soon logo grid; modal handles its own anonymous
  // visitor → /signup redirect.
  const [showRequestAccountModal, setShowRequestAccountModal] = useState(false);

  useEffect(() => {
    if (paused || HERO_SCENARIOS.length <= 1) return;
    const TOTAL_MS = 10000;
    const STEP_MS = 50;
    const interval = setInterval(() => {
      elapsedRef.current += STEP_MS;
      if (elapsedRef.current >= TOTAL_MS) {
        elapsedRef.current = 0;
        setScenarioIdx((i) => (i + 1) % HERO_SCENARIOS.length);
      }
      setTickProgress(elapsedRef.current / TOTAL_MS);
    }, STEP_MS);
    return () => clearInterval(interval);
  }, [paused]);

  const goToScenario = (i) => {
    elapsedRef.current = 0;
    setScenarioIdx(i);
    setTickProgress(0);
  };

  useEffect(() => {
    API.get("/accounts")
      .then(({ data }) => {
        if (Array.isArray(data?.accounts) && data.accounts.length) setAccounts(data.accounts);
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const primaryCtaTo = user ? "/intelligence" : "/signup";
  const primaryCtaLabel = user ? "Open Reveal" : "Set up your Reveal";

  const liveAccounts = accounts.filter((a) => a.status === "live");
  // Coming Soon list is hardcoded (see COMING_SOON above) — independent
  // of the API catalog. Anything currently `live` in the API is filtered
  // out as a guard, so if a "soon" account flips to live it disappears
  // from this list automatically.
  const liveIds = new Set(liveAccounts.map((a) => a.id));
  const soonAccounts = COMING_SOON.filter((a) => !liveIds.has(a.id));

  return (
    <div
      className="min-h-screen"
      style={{ color: "#24211d", backgroundColor: "#f6f0e6" }}
      data-testid="landing-page"
    >
      <GridBackground />

      {/*
        v6.5.1 — Hero word rotator (CSS-only, GPU-accelerated transform).
        Six words rotate through a fixed-height clipping window. Each word
        holds for ~2s, slides up with a 0.5s ease, next word lands on the
        same baseline. 15s cycle. Word #7 is a clone of word #1 so the
        wrap from "stop" back to "say" is seamless (no jump-back artefact).
        Respects prefers-reduced-motion: the rotator becomes static and
        shows the first word ("say"), so the headline still reads as
        "Test what to say for your account this week. Every week."

        Styles live in /app/frontend/src/styles/word-rotator.css and are
        imported at the top of this file (XSS-hygiene migration —
        previously delivered via <style dangerouslySetInnerHTML>).
      */}

      {/* ── Header ──────────────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-30"
        style={{ backgroundColor: "rgba(246, 240, 230, 0.92)", backdropFilter: "saturate(140%) blur(8px)", borderBottom: "1px solid #ddd3c2" }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Link to="/" data-testid="logo-link" aria-label="Kalib AI home">
              <KalibLogo variant="light" height={28} testId="landing-header-logo" />
            </Link>
            <BetaBadge />
          </div>
          <nav className="flex items-center gap-6 sm:gap-8">
            <Link
              to="/about"
              className="text-[11px] font-semibold uppercase transition-colors hover:opacity-80"
              style={{ letterSpacing: "0.15em", color: "#3a342e" }}
              data-testid="header-principles-link"
            >
              Principles
            </Link>
            {!user && (
              <Link
                to="/signin"
                className="text-[11px] font-semibold uppercase transition-colors"
                style={{ letterSpacing: "0.15em", color: "#3a342e" }}
                data-testid="header-signin-link"
              >
                Sign in
              </Link>
            )}
            <Link to={primaryCtaTo} data-testid="header-primary-cta-link">
              <Button
                className="rounded-none text-[13px] font-medium px-4 py-2 text-white"
                style={{ backgroundColor: "#d69b22" }}
              >
                {primaryCtaLabel}
                <ArrowRight className="w-3.5 h-3.5 ml-1.5 inline" strokeWidth={2.5} />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* ── HERO — two-column with amber arc + product card ──────────── */}
      <section className="relative pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 lg:items-stretch">
          {/* LEFT — eyebrow + headline + CTAs */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <HairlineRule />
              <Eyebrow>For Operators Covering Tier-1 FS Accounts</Eyebrow>
            </div>
            <h1
              className="font-heading tracking-tight leading-[1.05]"
              style={{ fontSize: "clamp(2.75rem, 5vw + 1rem, 4.5rem)", color: "#24211d", fontWeight: 700 }}
              data-testid="hero-headline"
            >
              Test what to{" "}
              <span className="kalib-word-rotator" data-testid="hero-rotating-word">
                <span className="kalib-word-rotator__inner">
                  <span className="kalib-word-rotator__item">say next</span>
                  <span className="kalib-word-rotator__item">shape now</span>
                  <span className="kalib-word-rotator__item">shift first</span>
                  <span className="kalib-word-rotator__item">sequence next</span>
                  <span className="kalib-word-rotator__item">stress-test now</span>
                  <span className="kalib-word-rotator__item">stop doing</span>
                  {/* clone of first phrase so the loop wraps invisibly */}
                  <span className="kalib-word-rotator__item" aria-hidden="true">say next</span>
                </span>
              </span>
              <br />
              in your accounts.
            </h1>
            <p
              className="mt-8 max-w-xl text-[16px] md:text-[17px]"
              style={{ color: "#3a342e", lineHeight: 1.65 }}
            >
              Every week, Kalib turns public signals from your target FS accounts into a calibrated Reveal of 3–5 moves.{" "}
              <span style={{ color: "#b8790f", fontWeight: 600 }}>Tuned to your commercial lane, role, and level.</span>
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:items-start">
              {/* v6.5.3 — single primary CTA. The right-side rotator
                  already carries the sample-Reveal job (5 cards, each
                  with its own "See this Reveal" link), so a hero "See
                  sample Reveal" button was duplicating that surface.
                  Header "Set up your Reveal" continues to follow as
                  visitors scroll; this in-context primary anchors the
                  hero. Trust microcopy sits directly below it. */}
              <div className="flex flex-col gap-2 w-full sm:w-auto">
                <Link to={primaryCtaTo} data-testid="hero-primary-cta">
                  <Button
                    className="rounded-none text-[14px] font-medium px-6 py-3 w-full sm:w-auto text-white hover:opacity-90"
                    style={{ backgroundColor: "#24211d" }}
                  >
                    {primaryCtaLabel}
                    <ArrowRight className="w-4 h-4 ml-2 inline" strokeWidth={2.5} />
                  </Button>
                </Link>
                <p
                  className="text-[12px]"
                  style={{ color: "#6f675c" }}
                  data-testid="hero-trust-line"
                >
                  90 sec. No firm name. No card. Public signals only.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT — amber arc + product artefact card */}
          <div className="lg:col-span-5 relative flex min-h-[440px]">
            <AmberArc className="absolute inset-0 w-full h-full -z-0" />

            {/*
              v6.5.2 — Card frame is now a <div>; the body content (top
              label + 3 sections) is wrapped in an <a> so right-click /
              middle-click "open in new tab" still works. The dot-pager
              is a sibling of the link (buttons inside <a> are invalid
              HTML5). Hover anywhere on the frame pauses the 10s
              countdown so visitors mid-read aren't yanked. Clicking a
              dot jumps to that scenario and resets the tick.
            */}
            <div
              className="relative z-10 mt-6 lg:mt-12 ml-auto w-full max-w-sm group transition-shadow duration-300 flex flex-col"
              style={{
                backgroundColor: "#fbf8f1",
                border: "1px solid #ddd3c2",
                padding: "26px",
                boxShadow: "0 1px 0 rgba(184, 121, 15, 0.05)",
              }}
              data-testid="hero-card-frame"
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 14px 32px -10px rgba(184, 121, 15, 0.18), 0 4px 8px -2px rgba(36, 33, 29, 0.05)";
                setPaused(true);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 1px 0 rgba(184, 121, 15, 0.05)";
                setPaused(false);
              }}
            >
              {/* Thin amber progress bar — pinned to top edge of card.
                  Width animates 0→100% over 10s; resets on advance/skip. */}
              <div
                className="absolute top-0 left-0 right-0"
                style={{ height: "2px", backgroundColor: "#f0e6d4" }}
                aria-hidden="true"
                data-testid="hero-card-progress-track"
              >
                <div
                  style={{
                    width: `${tickProgress * 100}%`,
                    height: "100%",
                    backgroundColor: "#d69b22",
                    transition: "width 50ms linear",
                  }}
                  data-testid="hero-card-progress-fill"
                />
              </div>

              <a
                href={scenario.sampleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block flex-1"
                style={{ textDecoration: "none", color: "inherit" }}
                data-testid="hero-card-link"
              >
                {/* Top label row */}
                <div className="flex items-center justify-between mb-5">
                  <Eyebrow>How {BRAND} Works</Eyebrow>
                  {scenario.topRightLogo ? (
                    <img
                      src={scenario.topRightLogo.src}
                      alt={scenario.topRightLogo.alt}
                      onError={(e) => {
                        // Fall back to text label if the brand mark CDN
                        // fails to serve. Avoids a broken-image glyph in
                        // production.
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget.nextSibling;
                        if (fallback) fallback.style.display = "inline";
                      }}
                      style={{
                        height: "20px",
                        width: "auto",
                        maxWidth: "70px",
                        objectFit: "contain",
                      }}
                      data-testid="hero-card-state-logo"
                    />
                  ) : null}
                  <span
                    className="text-[10px] uppercase tracking-widest font-medium"
                    style={{
                      color: "#6f675c",
                      letterSpacing: "0.15em",
                      display: scenario.topRightLogo ? "none" : "inline",
                    }}
                    data-testid="hero-card-state-tag"
                  >
                    Sample
                  </span>
                </div>

                {/* Section 1 — Signal this week */}
                <div className="pt-5" style={{ borderTop: "1px solid #e8dec9" }}>
                  <Eyebrow>{scenario.signalEyebrow}</Eyebrow>
                  <p
                    className="mt-2.5 text-[12.5px]"
                    style={{ color: "#24211d", lineHeight: 1.6 }}
                    data-testid="hero-card-signal"
                  >
                    {scenario.signal}
                  </p>
                </div>

                {/* Section 2 — You are */}
                <div className="mt-5 pt-5" style={{ borderTop: "1px solid #e8dec9" }}>
                  <Eyebrow>You are</Eyebrow>
                  <p
                    className="mt-2.5 text-[12.5px]"
                    style={{ color: "#24211d", lineHeight: 1.6 }}
                    data-testid="hero-card-you-are"
                  >
                    {scenario.youAre}
                  </p>
                </div>

                {/* Section 3 — 5 moves to test this week */}
                <div className="mt-5 pt-5" style={{ borderTop: "1px solid #e8dec9" }}>
                  <Eyebrow>5 Moves to Test This Week</Eyebrow>
                  {/*
                    Soft fade on the truncated first move. The mask-image
                    fades the bottom of the paragraph to 0 opacity so the
                    line reads as "continued in the linked Reveal".
                  */}
                  <p
                    className="mt-2.5 text-[12.5px]"
                    style={{
                      color: "#24211d",
                      lineHeight: 1.6,
                      WebkitMaskImage:
                        "linear-gradient(to bottom, #000 0%, #000 60%, transparent 100%)",
                      maskImage:
                        "linear-gradient(to bottom, #000 0%, #000 60%, transparent 100%)",
                    }}
                    data-testid="hero-card-move-snippet"
                  >
                    <span style={{ color: "#b8790f", fontWeight: 700 }}>1.</span>{" "}
                    {scenario.moveSnippet}
                  </p>
                </div>
              </a>

              {/* Bottom row — dot pager (left) + CTA (right). Both sit
                  outside the main <a> so dot clicks don't navigate. CTA
                  is its own <a> to keep the "See this Reveal" affordance
                  identical to the body link. */}
              <div className="mt-auto pt-6 flex items-center justify-between">
                {HERO_SCENARIOS.length > 1 ? (
                  <div
                    className="flex items-center gap-2"
                    role="tablist"
                    aria-label="Hero scenario pager"
                    data-testid="hero-card-pager"
                  >
                    {HERO_SCENARIOS.map((s, i) => {
                      const active = i === scenarioIdx;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          aria-label={`Show scenario ${i + 1}: ${s.id}`}
                          onClick={() => goToScenario(i)}
                          className="transition-all"
                          style={{
                            width: active ? "18px" : "6px",
                            height: "6px",
                            borderRadius: "3px",
                            backgroundColor: active ? "#d69b22" : "#ddd3c2",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                          }}
                          data-testid={`hero-card-pager-dot-${i}`}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <span />
                )}

                <a
                  href={scenario.sampleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[12.5px] font-medium transition-colors"
                  style={{ color: "#111111", textDecoration: "none" }}
                  data-testid="hero-card-cta"
                >
                  See this Reveal
                  <ArrowUpRight
                    className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={2.25}
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section: The other half of your account brain ─────────────── */}
      <section className="py-20 md:py-28" style={{ backgroundColor: "#fbf8f1", borderTop: "1px solid #ddd3c2", borderBottom: "1px solid #ddd3c2" }}>
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <HairlineRule />
              <Eyebrow>The Point</Eyebrow>
            </div>
            <h2 className="font-heading tracking-tight" style={{ fontSize: "clamp(2rem, 3vw + 1rem, 3.25rem)", lineHeight: 1.1, color: "#24211d", fontWeight: 700 }}>
              {BRAND}: The other half<br />of your account brain.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <div className="border-l-2 pl-5" style={{ borderColor: "#d69b22" }}>
              <p style={{ color: "#3a342e", fontSize: "16px", lineHeight: 1.7 }}>
                Kalib brings the outside-in view, calibrated to your offer lane, commercial goal, role, and target account. Fresh every week.
              </p>
              <p className="mt-4" style={{ color: "#3a342e", fontSize: "16px", lineHeight: 1.7 }}>
                You have the inside half — the politics, the relationships, the deals in motion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section: Change the lens ──────────────────────────────────── */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-5">
            <HairlineRule />
            <Eyebrow>Calibration in action</Eyebrow>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <h2 className="font-heading tracking-tight" style={{ fontSize: "clamp(1.75rem, 2.4vw + 1rem, 2.75rem)", lineHeight: 1.15, color: "#24211d", fontWeight: 700 }}>
                One account signal.<br />Different commercial moves.
              </h2>
              <p className="mt-6" style={{ color: "#3a342e", fontSize: "15px", lineHeight: 1.65, maxWidth: "30rem" }}>
                A cloud seller, services account manager, delivery lead and solutioning lead should not all get the same recommendation.
              </p>
            </div>

            <div className="lg:col-span-7 space-y-6">
              {LENS_GROUPS.map((group) => (
                <div key={group.header} className="grid grid-cols-12 gap-3 items-start">
                  <div className="col-span-12 sm:col-span-3">
                    <Eyebrow>{group.header}</Eyebrow>
                  </div>
                  <div className="col-span-12 sm:col-span-9 flex flex-wrap gap-2">
                    {group.items.map((item, idx) => (
                      <AmberPill key={item} active={idx === 2}>{item}</AmberPill>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Section: Top FS accounts ───────────────────────────────── */}
      <section className="py-20 md:py-28" style={{ backgroundColor: "#fbf8f1", borderTop: "1px solid #ddd3c2", borderBottom: "1px solid #ddd3c2" }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-5">
            <HairlineRule />
            <Eyebrow>Coverage</Eyebrow>
          </div>
          <h2 className="font-heading tracking-tight" style={{ fontSize: "clamp(2rem, 3vw + 1rem, 3rem)", lineHeight: 1.1, color: "#24211d", fontWeight: 700 }}>
            Top FS accounts.
          </h2>
          <p className="mt-5 max-w-2xl" style={{ color: "#3a342e", fontSize: "16px", lineHeight: 1.65 }}>
            Built for technology vendors defending ground, expanding scope, or winning into major financial-services accounts.
          </p>

          <div className="mt-10">
            <Eyebrow>Live Now</Eyebrow>
            {/* Logo-first tiles, same hover-reveal pattern as Coming Soon
                but at full saturation to communicate active coverage. A
                small amber dot in the top-right corner of each tile
                indicates live status (replaces the inline dot from the
                old text-pill version). */}
            <div className="mt-3 flex flex-wrap gap-2">
              {liveAccounts.map((a) => {
                const logo = LIVE_LOGOS[a.id];
                if (!logo) {
                  return (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-[13px]"
                      style={{ backgroundColor: "#fdf3df", border: "1px solid #d69b22", color: "#24211d", fontWeight: 500 }}
                      data-testid={`live-account-${a.id}`}
                    >
                      <span className="w-1.5 h-1.5" style={{ backgroundColor: "#d69b22", borderRadius: "50%" }} />
                      {a.name}
                    </span>
                  );
                }
                return (
                  <div
                    key={a.id}
                    className="group relative inline-flex items-center justify-center transition-all"
                    style={{
                      width: "120px",
                      height: "44px",
                      backgroundColor: "#fdf3df",
                      border: "1px solid #d69b22",
                    }}
                    title={a.name}
                    data-testid={`live-account-${a.id}`}
                    tabIndex={0}
                    aria-label={`${a.name} — Live now`}
                  >
                    {/* Live status dot — top-right corner. Sits above the
                        logo so it stays visible regardless of hover state. */}
                    <span
                      aria-hidden="true"
                      className="absolute"
                      style={{
                        top: "6px",
                        right: "6px",
                        width: "5px",
                        height: "5px",
                        backgroundColor: "#d69b22",
                        borderRadius: "50%",
                      }}
                    />
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className="transition-opacity duration-200 group-hover:opacity-0 group-focus-visible:opacity-0"
                      style={{
                        maxHeight: "22px",
                        maxWidth: "90px",
                        objectFit: "contain",
                      }}
                    />
                    <span
                      className="absolute inset-0 flex items-center justify-center text-[13px] opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100 px-2 text-center"
                      style={{ color: "#24211d", fontWeight: 500 }}
                      data-testid={`live-account-name-${a.id}`}
                    >
                      {a.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-7">
            <Eyebrow>Coming Soon</Eyebrow>
            {/* Logo-first tiles. Account name is hidden in the visual and
                surfaces only on hover (or focus, for keyboard users) via
                an absolutely-positioned label. The accessible name lives
                on the <img alt> + the visually-hidden span so screen
                readers and tooltips both work. */}
            <div className="mt-3 flex flex-wrap gap-2">
              {soonAccounts.map((a) => {
                const logo = a.logo ? { src: a.logo, alt: a.name } : null;
                if (!logo) {
                  return (
                    <span
                      key={a.id}
                      className="inline-block px-3 py-1.5 text-[13px]"
                      style={{ backgroundColor: "#fbf8f1", border: "1px solid #ddd3c2", color: "#6f675c" }}
                      data-testid={`soon-account-${a.id}`}
                    >
                      {a.name}
                    </span>
                  );
                }
                return (
                  <div
                    key={a.id}
                    className="group relative inline-flex items-center justify-center transition-all"
                    style={{
                      width: "120px",
                      height: "44px",
                      backgroundColor: "#fbf8f1",
                      border: "1px solid #ddd3c2",
                    }}
                    title={a.name}
                    data-testid={`soon-account-${a.id}`}
                    tabIndex={0}
                    aria-label={`${a.name} — Coming soon`}
                  >
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className="transition-opacity duration-200 group-hover:opacity-0 group-focus-visible:opacity-0"
                      style={{
                        maxHeight: "22px",
                        maxWidth: "90px",
                        objectFit: "contain",
                        filter: "grayscale(100%) brightness(0.6)",
                      }}
                    />
                    <span
                      className="absolute inset-0 flex items-center justify-center text-[13px] opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100 px-2 text-center"
                      style={{ color: "#24211d", fontWeight: 500 }}
                      data-testid={`soon-account-name-${a.id}`}
                    >
                      {a.name}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Inline Request Account CTA — sits directly below the
                Coming Soon logo grid. Subtle text-link affordance to
                match the Coverage section's restrained tone (no big
                button, no card wrap). The amber "Request here" picks
                up the same accent colour we use for hero tune-ables
                and other in-context links so visitors recognise it
                as interactive. */}
            <p
              className="mt-5 text-[13px]"
              style={{ color: "#3a342e" }}
              data-testid="coverage-request-account-line"
            >
              Don't see your account?{" "}
              <button
                type="button"
                onClick={() => setShowRequestAccountModal(true)}
                className="font-semibold underline-offset-2 hover:underline transition-colors"
                style={{ color: "#b8790f" }}
                data-testid="coverage-request-account-link"
              >
                Request here
              </button>
              .
            </p>
          </div>
        </div>
      </section>

      {/* ── Section: Outside + Inside (two columns + divider) ────────── */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-5">
            <HairlineRule />
            <Eyebrow>Outside + Inside</Eyebrow>
          </div>
          <h2 className="font-heading tracking-tight max-w-3xl mb-12" style={{ fontSize: "clamp(2rem, 3vw + 1rem, 3.25rem)", lineHeight: 1.1, color: "#24211d", fontWeight: 700 }}>
            Kalib reads the outside.<br />You judge the inside.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ backgroundColor: "#ddd3c2" }}>
            <div className="p-8 md:p-10" style={{ backgroundColor: "#fbf8f1" }}>
              <Eyebrow>Kalib reads</Eyebrow>
              <ul className="mt-5 space-y-3">
                {COMPARISON_ROWS.map((row) => (
                  <li key={row.outside} className="flex items-start gap-2.5 text-[15px]" style={{ color: "#24211d" }}>
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#d69b22" }} strokeWidth={2.5} />
                    <span>{row.outside}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-8 md:p-10" style={{ backgroundColor: "#fbf8f1" }}>
              <Eyebrow>Only you know</Eyebrow>
              <ul className="mt-5 space-y-3">
                {COMPARISON_ROWS.map((row) => (
                  <li key={row.inside} className="flex items-start gap-2.5 text-[15px]" style={{ color: "#24211d" }}>
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#d69b22" }} strokeWidth={2.5} />
                    <span>{row.inside}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-10 text-center font-medium text-[16px]" style={{ color: "#24211d" }}>
            The call on which play to run this week is yours.
          </p>
        </div>
      </section>

      {/* ── Section: Built from field experience ─────────────────────── */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-5">
            <HairlineRule />
            <Eyebrow>Field Credibility</Eyebrow>
          </div>
          <h2 className="font-heading tracking-tight max-w-3xl mb-12" style={{ fontSize: "clamp(2rem, 3vw + 1rem, 3.25rem)", lineHeight: 1.1, color: "#24211d", fontWeight: 700 }}>
            Built from field experience.
          </h2>
          {/* Single-card founder block. If a second credibility card
              is ever added, wrap this in the same gap-px grid pattern
              used elsewhere on the page (see Live Now / Coming Soon). */}
          <div
            className="p-7"
            style={{ backgroundColor: "#fbf8f1", border: "1px solid #ddd3c2" }}
            data-testid="founder-chip"
          >
            <div className="flex items-center gap-4 mb-4">
              <img
                src={FOUNDER.image}
                alt={FOUNDER.name}
                className="flex-shrink-0"
                style={{
                  width: "72px",
                  height: "72px",
                  objectFit: "cover",
                  border: "1px solid #d69b22",
                }}
                data-testid="founder-image"
              />
              <div className="flex flex-col gap-1.5 min-w-0">
                <p
                  className="font-heading"
                  style={{ fontSize: "18px", fontWeight: 700, color: "#24211d", lineHeight: 1.2 }}
                  data-testid="founder-name"
                >
                  {FOUNDER.name}
                </p>
                <a
                  href={FOUNDER.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  // 2026-05-09 — explicitly open via window.open at the
                  // top context so the click works inside Emergent's
                  // preview iframe (LinkedIn sets X-Frame-Options: DENY,
                  // which makes a default-target='_blank' click look
                  // like "Refused to connect" if the host browser tries
                  // to navigate the iframe instead of spawning a tab).
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(FOUNDER.linkedin, "_blank", "noopener,noreferrer");
                  }}
                  className="inline-flex items-center gap-1.5 text-[12px] font-medium transition-colors hover:opacity-80 self-start px-2 py-1"
                  style={{
                    color: "#24211d",
                    backgroundColor: "#fdf3df",
                    border: "1px solid #d69b22",
                  }}
                  aria-label={`${FOUNDER.name} on LinkedIn`}
                  data-testid="founder-linkedin"
                >
                  <Linkedin className="w-3 h-3" strokeWidth={2} />
                  LinkedIn
                </a>
              </div>
            </div>
            <div className="space-y-3 max-w-3xl">
              {FOUNDER.paragraphs.map((p, i) => {
                const isAccent = i === FOUNDER.paragraphs.length - 1;
                return (
                  <p
                    key={i}
                    className="text-[14px]"
                    style={{
                      color: isAccent ? "#b8790f" : "#24211d",
                      lineHeight: 1.55,
                      fontWeight: isAccent ? 700 : 400,
                    }}
                  >
                    {p}
                  </p>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── Section: Final CTA ───────────────────────────────────────── */}
      <section className="relative py-24 md:py-32 overflow-hidden" style={{ backgroundColor: "#fbf8f1", borderTop: "1px solid #ddd3c2" }}>
        {/* huge faint amber circle in the bottom-right */}
        <div
          className="absolute -bottom-24 -right-24 w-[480px] h-[480px] pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(214,155,34,0.16), transparent 70%)" }}
        />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 mb-6">
            <HairlineRule />
            <Eyebrow>Beta Access</Eyebrow>
            <HairlineRule />
          </div>
          <h2 className="font-heading tracking-tight" style={{ fontSize: "clamp(2.25rem, 4vw + 1rem, 3.75rem)", lineHeight: 1.05, color: "#24211d", fontWeight: 700 }}>
            Set your lens. Open your Reveal.
          </h2>
          <p className="mt-6 max-w-xl mx-auto" style={{ color: "#3a342e", fontSize: "16px", lineHeight: 1.65 }}>
            About 90 seconds of setup. You can correct the lens after seeing your first Reveal.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={primaryCtaTo} data-testid="final-cta-primary-link">
              <Button
                className="rounded-none text-[14px] font-medium px-7 py-3 text-white w-full sm:w-auto"
                style={{ backgroundColor: "#d69b22" }}
              >
                {primaryCtaLabel}
                <ArrowRight className="w-4 h-4 ml-2 inline" strokeWidth={2.5} />
              </Button>
            </Link>
            <Link to="/sample" data-testid="final-cta-sample-link">
              <Button
                variant="outline"
                className="rounded-none text-[14px] font-medium px-7 py-3 w-full sm:w-auto"
                style={{ borderColor: "#24211d", color: "#24211d", backgroundColor: "transparent" }}
              >
                See sample Reveal
              </Button>
            </Link>
          </div>
          <p className="mt-6 text-[12px]" style={{ color: "#6f675c" }}>
            Beta access. Public-signal intelligence only. No private client data required.
          </p>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer style={{ backgroundColor: "#f6f0e6", borderTop: "1px solid #ddd3c2" }}>
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <KalibLogo variant="light" height={22} testId="landing-footer-logo" />
          </div>
          <p className="text-[12px]" style={{ color: "#6f675c" }}>
            Commercial calibration for FS account teams.
          </p>
          <a
            href="#top"
            className="text-[10px] uppercase font-semibold"
            style={{ letterSpacing: "0.18em", color: "#6f675c" }}
          >
            Back to top
          </a>
        </div>
      </footer>

      {/* Request Account modal — mounted at root so the dialog overlays
          the entire landing page regardless of which section triggered
          it. Component handles its own open/close + anonymous→signup
          flow. */}
      <RequestAccountModal
        open={showRequestAccountModal}
        onClose={() => setShowRequestAccountModal(false)}
      />
    </div>
  );
}
